import { useState } from 'react';
import { useBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { getModelFromSnapshot } from 'lib/model';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { getFirebaseContext } from 'utils/firebase';
import { EOrganization, ESnapshotExists } from 'lib/types';
import { TextField } from 'lib/components/TextField';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { SearchableOrganizationRecord } from 'lib/types/searchable';
import { PublisherLocationFilter } from './PublisherLocationFilter';
import {
  AdjudicationFilters,
  PublisherFilters
} from '../helpers/publisherSearch';
import { PublisherSearchResultsList } from './PublisherSearchResultsList';
import { SelectedPublisherCard } from './SelectedPublisherCard';
import PublisherNetworkTag from './PublisherNetworkTag';
import NetworkInfoModal from './NetworkInfoModal';

type FastConfirmPublisherFormProps = {
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  onPublisherOrganizationChange: (publisherOrganizationId: string) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  publisherOptions: SearchableOrganizationRecord[];
  stateOptions: number[];
  onStateChange: (stateId?: number) => void;
  onAdjudicationFilterChange: (filters: AdjudicationFilters) => void;
  activeFilters: PublisherFilters;
  loading: boolean;
  columnCertifiedFilter: boolean;
  setColumnCertifiedFilter: (value: boolean) => void;
};

export default function FastConfirmPublisherForm({
  publisherOrganization,
  onPublisherOrganizationChange,
  searchText,
  setSearchText,
  publisherOptions,
  stateOptions,
  onStateChange,
  onAdjudicationFilterChange,
  activeFilters,
  loading,
  columnCertifiedFilter,
  setColumnCertifiedFilter
}: FastConfirmPublisherFormProps) {
  const [showNetworkInfoModal, setShowNetworkInfoModal] = useState(false);

  const showExternalPublisherFilter = useBooleanFlag(
    LaunchDarklyFlags.ENABLE_EXTERNAL_PUBLISHER_FILTER,
    false
  );

  const publisherOrganizationModel = publisherOrganization
    ? getModelFromSnapshot(
        OrganizationModel,
        getFirebaseContext(),
        publisherOrganization
      )
    : undefined;

  return (
    <>
      {publisherOrganizationModel && (
        <SelectedPublisherCard
          publisherOrganizationModel={publisherOrganizationModel}
          onPublisherOrganizationChange={onPublisherOrganizationChange}
          tags={
            showExternalPublisherFilter
              ? [
                  <PublisherNetworkTag
                    key="network-tag"
                    isExtendedNetwork={publisherOrganizationModel.isExpress}
                    onClick={() => setShowNetworkInfoModal(true)}
                  />
                ]
              : []
          }
        />
      )}

      {!publisherOrganization && (
        <>
          <div className="space-y-3">
            <TextField
              id="publisher-search"
              labelText="Search for publisher by name or location"
              placeholder={`Search "Express Times" or "Essex County" or "Kansas"`}
              prefix={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
              value={searchText}
              onChange={setSearchText}
            />
            <div className="flex justify-end">
              <PublisherLocationFilter
                stateOptions={stateOptions}
                onStateChange={onStateChange}
                activeFilters={activeFilters}
                onAdjudicationFilterChange={onAdjudicationFilterChange}
              />
            </div>
          </div>

          <PublisherSearchResultsList
            publisherOptions={publisherOptions}
            loading={loading}
            activeFilters={activeFilters}
            searchText={searchText}
            columnCertifiedFilter={columnCertifiedFilter}
            setColumnCertifiedFilter={setColumnCertifiedFilter}
            onPublisherOrganizationChange={onPublisherOrganizationChange}
            setShowNetworkInfoModal={setShowNetworkInfoModal}
          />
        </>
      )}

      {showNetworkInfoModal && (
        <NetworkInfoModal onClose={() => setShowNetworkInfoModal(false)} />
      )}
    </>
  );
}
